import invariant from 'invariant';
import type { Asset, Comment, Share } from '@frameio/graphql-client/generated';
// @todo (WEB-1045): Add condensed UUID support to routes
// import { toCondensedUUID } from '@frameio/utils/formatters/uuid';
import { createUrl } from '../helpers/createUrl';
import { getPathnameFromHref } from '../helpers/getPathnameFromHref';
import type { ValidHref } from '../types';

// @todo (WEB-1045): Remove when condensed UUID support is added to routes
const toCondensedUUID = (uuid: string): string => uuid;

/**
 * ShareReview Reel Layout
 * /share/:shareId/reel
 */
type ShareReviewReelPage = {
  page: 'shareReviewReel';
  shareId: Share['id'];
};

/**
 * ShareReview Reel Layout (with specific asset active)
 * /share/:shareId/reel/:assetId
 */
type ShareReviewReelPageWithAsset = {
  page: 'shareReviewReel';
  shareId: Share['id'];
  assetId: Asset['id'];
};

/**
 * ShareReview Reel Layout nested asset (with specific asset active)
 * /share/:shareId/reel/:assetId/:nestedAssetId
 */
type ShareReviewReelPageWithNestedAsset = {
  page: 'shareReviewReel';
  shareId: Share['id'];
  assetId: Asset['id'];
  nestedAssetId: Asset['id'];
};

type OptionalQueryParams = {
  commentId?: Comment['id'];
  versionId?: Asset['id'];
};

export type ShareReviewReelGetLinkParams = (
  | ShareReviewReelPage
  | ShareReviewReelPageWithAsset
  | ShareReviewReelPageWithNestedAsset
) &
  OptionalQueryParams;

export const getLinkForShareReviewReel = (
  params: ShareReviewReelGetLinkParams
): ValidHref => {
  invariant(params.shareId, 'shareId is required');
  const shareId = toCondensedUUID(params.shareId);

  const q = new URLSearchParams();
  let queryParams = '';

  if (params.commentId) {
    q.set('c', params.commentId);
    queryParams = `?${q}`;
  }

  if (params.versionId) {
    q.set('version', params.versionId);
    queryParams = `?${q}`;
  }

  // /share/:shareId/reel/:assetId/:nestedAssetId(?c=:commentId)(version=:versionId)
  if ('nestedAssetId' in params && params.nestedAssetId) {
    const assetId = toCondensedUUID(params.assetId);
    const nestedAssetId = toCondensedUUID(params.nestedAssetId);
    return createUrl({
      pathname: `/share/${shareId}/reel/${assetId}/${nestedAssetId}${queryParams}`,
    });
  }

  // /share/:shareId/reel/:assetId(?c=:commentId)(version=:versionId)
  if ('assetId' in params && params.assetId) {
    const assetId = toCondensedUUID(params.assetId);
    return createUrl({
      pathname: `/share/${shareId}/reel/${assetId}${queryParams}`,
    });
  }

  // Needed for empty reels
  return createUrl({
    pathname: `/share/${shareId}/reel`,
  });
};

export const isShareReviewReelPageType = (href: ValidHref): boolean => {
  const pathname = getPathnameFromHref(href);
  const parts = pathname.split('/');
  return parts[1] === 'share' && parts[3] === 'reel';
};
